import { FRAG_COPY, VERTEX_FULLSCREEN } from '../shaders/glsl';

export class WebGLShaderProgram {
  readonly gl: WebGL2RenderingContext;
  readonly program: WebGLProgram;
  private readonly uniforms = new Map<string, WebGLUniformLocation | null>();

  constructor(gl: WebGL2RenderingContext, vertexSrc: string, fragmentSrc: string) {
    this.gl = gl;
    const vs = this.compile(gl.VERTEX_SHADER, vertexSrc);
    const fs = this.compile(gl.FRAGMENT_SHADER, fragmentSrc);
    const prog = gl.createProgram();
    if (!prog) throw new Error('Failed to create shader program');
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      throw new Error(`Program link failed: ${gl.getProgramInfoLog(prog)}`);
    }
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    this.program = prog;
  }

  private compile(type: number, source: string): WebGLShader {
    const gl = this.gl;
    const shader = gl.createShader(type);
    if (!shader) throw new Error('Failed to create shader');
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      throw new Error(`Shader compile failed: ${gl.getShaderInfoLog(shader)}`);
    }
    return shader;
  }

  use(): void {
    this.gl.useProgram(this.program);
  }

  private loc(name: string): WebGLUniformLocation | null {
    if (!this.uniforms.has(name)) {
      this.uniforms.set(name, this.gl.getUniformLocation(this.program, name));
    }
    return this.uniforms.get(name) ?? null;
  }

  setUniform1f(name: string, v: number): void {
    const loc = this.loc(name);
    if (loc) this.gl.uniform1f(loc, v);
  }

  setUniform1i(name: string, v: number): void {
    const loc = this.loc(name);
    if (loc) this.gl.uniform1i(loc, v);
  }

  setUniform2f(name: string, x: number, y: number): void {
    const loc = this.loc(name);
    if (loc) this.gl.uniform2f(loc, x, y);
  }

  setUniform3f(name: string, x: number, y: number, z: number): void {
    const loc = this.loc(name);
    if (loc) this.gl.uniform3f(loc, x, y, z);
  }

  dispose(): void {
    this.gl.deleteProgram(this.program);
  }
}

let quadVao: WebGLVertexArrayObject | null = null;
let quadBuffer: WebGLBuffer | null = null;

/** Fullscreen triangle pair for post-process passes. */
export function ensureFullscreenQuad(gl: WebGL2RenderingContext): void {
  if (quadVao && quadBuffer) return;
  const positions = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
  quadBuffer = gl.createBuffer();
  quadVao = gl.createVertexArray();
  if (!quadBuffer || !quadVao) throw new Error('Failed to create fullscreen quad');
  gl.bindVertexArray(quadVao);
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);
  const prog = gl.createProgram(); // temp — actual attrib bound per draw via current program
  void prog;
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
}

export function drawFullscreenQuad(gl: WebGL2RenderingContext, program: WebGLShaderProgram): void {
  ensureFullscreenQuad(gl);
  program.use();
  const loc = gl.getAttribLocation(program.program, 'a_position');
  gl.bindVertexArray(quadVao);
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}

export function createGpuContext(canvas: HTMLCanvasElement): {
  gl: WebGL2RenderingContext;
  copyProgram: WebGLShaderProgram;
} | null {
  const gl = canvas.getContext('webgl2', {
    alpha: true,
    premultipliedAlpha: false,
    preserveDrawingBuffer: true,
  });
  if (!gl) return null;
  const copyProgram = new WebGLShaderProgram(gl, VERTEX_FULLSCREEN, FRAG_COPY);
  return { gl, copyProgram };
}
